import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useQueryStates, createParser } from "nuqs";
import { format, parse, isValid } from "date-fns";

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  formatForApi: () => { startDate: string; endDate: string };
  formatForDisplay: () => string;
}

const DateRangeContext = createContext<DateRangeContextType | null>(null);

// Custom date parser that treats dates as LOCAL time (not UTC)
const parseAsLocalDate = createParser({
  parse: (value: string) => {
    // Parse as local date by using date-fns parse with a reference date
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
  },
  serialize: (date: Date) => {
    return format(date, "yyyy-MM-dd");
  },
});

// Default start date - All Time (from year 2000)
const defaultStartDate = new Date(2000, 0, 1);

export function DateRangeProvider({ children }: { children: ReactNode }) {
  // Use nuqs to sync date range with URL query params
  // NOTE: endDate intentionally has NO withDefault — we compute it as new Date()
  // at render time so "today" is always accurate, even if the tab was opened days ago.
  const [queryDates, setQueryDates] = useQueryStates(
    {
      startDate: parseAsLocalDate.withDefault(defaultStartDate),
      endDate: parseAsLocalDate,
    },
    {
      history: "push",
    }
  );

  const dateRange: DateRange = {
    startDate: queryDates.startDate,
    endDate: queryDates.endDate ?? new Date(), // Always TODAY if no URL param
  };

  const setDateRange = (range: DateRange) => {
    setQueryDates({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const formatForApi = useCallback(() => ({
    startDate: format(dateRange.startDate, "yyyy-MM-dd"),
    endDate: format(dateRange.endDate, "yyyy-MM-dd"),
  }), [dateRange.startDate, dateRange.endDate]);

  const formatForDisplay = () => {
    // Show "All Time" if start date is Jan 1, 2000 or earlier
    if (dateRange.startDate.getFullYear() <= 2000) {
      return "All Time";
    }
    return `${format(dateRange.startDate, "MMM d, yyyy")} - ${format(dateRange.endDate, "MMM d, yyyy")}`;
  };

  return (
    <DateRangeContext.Provider value={{ dateRange, setDateRange, formatForApi, formatForDisplay }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
}
